import { forwardRef, type ReactNode } from 'react';
import {
  Button as MantineButton,
  type ButtonProps as MantineButtonProps,
  Loader,
} from '@mantine/core';
import { alpha, colors, inks } from '../../theme/tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ForgeButtonProps
  extends Omit<MantineButtonProps, 'variant' | 'size' | 'color' | 'leftSection' | 'rightSection'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Native tooltip — used e.g. for "Coming soon" on disabled near-term actions. */
  title?: string;
  children?: ReactNode;
}

const SIZE_HEIGHT: Record<ButtonSize, number> = { xs: 24, sm: 30, md: 36, lg: 42 };

interface VariantPalette {
  bg: string;
  bgHover: string;
  fg: string;
  border: string;
}

const VARIANT_TO_PALETTE: Record<ButtonVariant, VariantPalette> = {
  // Green accent with dark ink — reads in both themes (green is a light accent).
  primary: {
    bg: colors.neon,
    bgHover: 'rgb(var(--c-neon-bright))',
    fg: '#04140a',
    border: colors.neon,
  },
  secondary: {
    bg: 'transparent',
    bgHover: alpha('neon', 0.12),
    // Accent INK, not the raw accent: `colors.neon` on the light page background
    // is ~3.6:1 — under AA for a 14px/600 label. Ink aliases back in dark mode.
    fg: inks.neon,
    border: colors.neon,
  },
  ghost: {
    bg: 'transparent',
    bgHover: 'rgb(var(--c-card-hover))',
    fg: 'rgb(var(--c-txt))',
    border: 'transparent',
  },
  // Solid red with WHITE ink — fixed (not the theme-flipping --c-red token) so a
  // destructive button is unmistakably red and legible in light AND dark.
  danger: {
    bg: '#dc2626',
    bgHover: '#b91c1c',
    fg: '#ffffff',
    border: '#dc2626',
  },
};

/**
 * Button — primary call-to-action primitive.
 *
 * Composes Mantine `Button` with Forge variants. Loading state replaces the
 * left icon with a spinner and disables the button. The component preserves
 * Mantine's full keyboard / focus-ring story (handled by the base primitive).
 */
export const Button = forwardRef<HTMLButtonElement, ForgeButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled,
    leftIcon,
    rightIcon,
    children,
    style,
    type = 'button',
    onClick,
    ...rest
  },
  ref,
) {
  const palette = VARIANT_TO_PALETTE[variant];
  const isInactive = disabled || loading;
  const height = SIZE_HEIGHT[size];

  return (
    <MantineButton
      ref={ref}
      size={size}
      type={type}
      onClick={onClick}
      disabled={isInactive}
      leftSection={loading ? <Loader size={14} color={palette.fg} /> : leftIcon}
      rightSection={rightIcon}
      style={{
        backgroundColor: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        height,
        fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
        fontWeight: 600,
        letterSpacing: 'normal',
        transition: 'background-color 120ms ease, transform 120ms ease',
        opacity: isInactive ? 0.55 : 1,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!isInactive) {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = palette.bgHover;
        }
      }}
      onMouseLeave={(e) => {
        if (!isInactive) {
          (e.currentTarget as HTMLButtonElement).style.backgroundColor = palette.bg;
        }
      }}
      {...rest}
    >
      {children}
    </MantineButton>
  );
});
